import axios from 'axios';
import { getAuthHeader } from './auth';
import { API_BASE_URL } from '../config/api';

export interface TemplateField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'date' | 'number';
  required: boolean;
  options?: string[];
  placeholder?: string;
  defaultValue?: string;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
  };
}

export interface TemplateSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  fields: TemplateField[];
  collapsed?: boolean;
}

export interface Template {
  id: string;
  name: string;
  description: string;
  type: 'community' | 'organization' | 'personal';
  organizationId?: string;
  createdBy: string;
  sections: TemplateSection[];
  isShared: boolean;
  isActive: boolean;
  usageCount: number;
  category: string;
  tags: string[];
  version: string;
  canEdit: boolean;
  canView: boolean;
  accessLevel: string;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: 'community' | 'organization' | 'personal' | 'all';
  category?: string;
  tags?: string[];
  isShared?: boolean;
  createdBy?: string;
  sortBy?: 'name' | 'createdAt' | 'usageCount' | 'lastUsed';
  sortOrder?: 'asc' | 'desc';
}

export interface TemplateResult {
  templates: Template[];
  total: number;
  page: number;
  limit: number;
  categories: string[];
  tags: string[];
}

export interface CreateTemplateData {
  name: string;
  description: string;
  type: 'organization' | 'personal';
  sections: TemplateSection[];
  category: string;
  tags?: string[];
  version?: string;
}

export interface UpdateTemplateData {
  name?: string;
  description?: string;
  sections?: TemplateSection[];
  category?: string;
  tags?: string[];
  version?: string;
  isActive?: boolean;
}

class TemplateService {
  private baseUrl = `${API_BASE_URL}/v1`;

  async getTemplates(filters: TemplateFilters = {}): Promise<TemplateResult> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(`${key}[]`, v));
        } else {
          params.append(key, String(value));
        }
      }
    });

    const response = await axios.get<{ success: boolean; data: TemplateResult }>(
      `${this.baseUrl}/templates?${params}`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error('Failed to fetch templates');
  }

  async getTemplate(templateId: string): Promise<Template> {
    const response = await axios.get<{ success: boolean; data: Template }>(
      `${this.baseUrl}/templates/${templateId}`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error('Failed to fetch template');
  }

  async createTemplate(data: CreateTemplateData): Promise<Template> {
    const response = await axios.post<{ success: boolean; data: Template }>(
      `${this.baseUrl}/templates`,
      data,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error('Failed to create template');
  }

  async updateTemplate(templateId: string, data: UpdateTemplateData): Promise<Template> {
    const response = await axios.put<{ success: boolean; data: Template }>(
      `${this.baseUrl}/templates/${templateId}`,
      data,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error('Failed to update template');
  }

  async deleteTemplate(templateId: string): Promise<void> {
    const response = await axios.delete<{ success: boolean }>(
      `${this.baseUrl}/templates/${templateId}`,
      { headers: getAuthHeader() }
    );
    
    if (!response.data.success) {
      throw new Error('Failed to delete template');
    }
  }

  async shareTemplate(templateId: string): Promise<Template> {
    const response = await axios.post<{ success: boolean; data: Template }>(
      `${this.baseUrl}/templates/${templateId}/share`,
      {},
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error('Failed to share template');
  }

  async unshareTemplate(templateId: string): Promise<Template> {
    // Note: Unsharing is not supported - sharing converts personal to org template
    throw new Error('Once a template is shared to the organization, it cannot be unshared. You can delete your personal copy, but the organization template will remain.');
  }

  async duplicateTemplate(templateId: string, newName?: string): Promise<Template> {
    const data = newName ? { name: newName } : {};
    const response = await axios.post<{ success: boolean; data: Template }>(
      `${this.baseUrl}/templates/${templateId}/duplicate`,
      data,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    throw new Error('Failed to duplicate template');
  }

  async useTemplate(templateId: string): Promise<void> {
    try {
      await axios.post(
        `${this.baseUrl}/templates/${templateId}/use`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
      // Usage tracking is non-critical, don't throw
      console.warn('Failed to track template usage:', error);
    }
  }

  async syncCommunityTemplates(): Promise<void> {
    const response = await axios.post<{ success: boolean }>(
      `${this.baseUrl}/templates/sync`,
      {},
      { headers: getAuthHeader() }
    );
    
    if (!response.data.success) {
      throw new Error('Failed to sync community templates');
    }
  }

  async getCategories(): Promise<string[]> {
    const response = await axios.get<{ success: boolean; data: string[] }>(
      `${this.baseUrl}/templates/metadata/categories`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    return [];
  }

  async getTags(): Promise<string[]> {
    const response = await axios.get<{ success: boolean; data: string[] }>(
      `${this.baseUrl}/templates/metadata/tags`,
      { headers: getAuthHeader() }
    );
    
    if (response.data.success) {
      return response.data.data;
    }
    
    return [];
  }

  // Utility methods for template validation
  validateTemplate(template: CreateTemplateData | UpdateTemplateData): string[] {
    const errors: string[] = [];
    
    if ('name' in template && template.name && template.name.trim().length < 1) {
      errors.push('Template name is required');
    }
    
    if ('description' in template && template.description && template.description.trim().length < 1) {
      errors.push('Template description is required');
    }
    
    if ('sections' in template && template.sections) {
      if (template.sections.length === 0) {
        errors.push('Template must have at least one section');
      }
      
      template.sections.forEach((section, sectionIndex) => {
        if (!section.title.trim()) {
          errors.push(`Section ${sectionIndex + 1} must have a title`);
        }
        
        if (section.fields.length === 0) {
          errors.push(`Section "${section.title}" must have at least one field`);
        }
        
        section.fields.forEach((field, fieldIndex) => {
          if (!field.label.trim()) {
            errors.push(`Field ${fieldIndex + 1} in section "${section.title}" must have a label`);
          }
          
          if (field.type === 'select' && (!field.options || field.options.length === 0)) {
            errors.push(`Select field "${field.label}" must have options`);
          }
        });
      });
    }
    
    return errors;
  }

  // Generate unique field ID
  generateFieldId(label: string): string {
    return label.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50) + '_' + Date.now();
  }

  // Generate unique section ID
  generateSectionId(title: string): string {
    return title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50) + '_' + Date.now();
  }
}

export const templateService = new TemplateService();